import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { UpsellingBanner as BaseBanner } from '@factorialco/f0-react'
import {
  CanSeeBlackFridayPromoDocument,
  CanSelfUpsellDocument,
  ReferralProgramPoliciesDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import AmazonCards from 'modules/referral_program/lib/assets/banner_amazon_cards.png'
import BlackFridayBannerImage from 'modules/referral_program/lib/assets/bf_marketplace_banner.png'
import BannerImage from 'modules/referral_program/lib/assets/iphone_air.png'
import MarketplaceBannerImage from 'modules/referral_program/lib/assets/marketplace_banner.png'

const STORAGE_KEY = 'referral_program_banner_state'

interface BannerState {
  closedAt?: number
  permanentlyDismissed?: boolean
}

const ReferalProgramBanner: React.FC = () => {
  const [bannerState, setBannerState] = useLocalStorage<BannerState>(STORAGE_KEY, {})
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { company, canRequestUpsell } = useCurrentSession()
  const { data: policies } = useQuery(ReferralProgramPoliciesDocument)
  const { data: selfUpsellPolicies } = useQuery(CanSelfUpsellDocument)
  const { data: blackFridayPromoData } = useQuery(CanSeeBlackFridayPromoDocument)
  const canSeeReferralPage = policies?.apiCore._policies.seeReferralProgramPage.can
  const canSelfUpsell = selfUpsellPolicies?.billing._policies.subscriptionUpgradeSubscription.can
  const canSeePromo =
    blackFridayPromoData?.upsell._policies.canSeeBlackFridayPromo.can &&
    !company.subscription?.inFreeTrial &&
    canSelfUpsell
  const isBrazil = company.legalEntities.find((e) => e.main)?.country === 'br'

  const shouldShow = React.useMemo(() => {
    if (bannerState?.permanentlyDismissed) {
      return false
    }
    if (!bannerState?.closedAt) {
      return true
    }

    const now = Date.now()
    const daysSinceClosed = (now - bannerState.closedAt) / (24 * 60 * 60 * 1000)
    return daysSinceClosed >= 15
  }, [bannerState])

  const handleClose = () => {
    track(trackingEvents.settings.dimissSettingsReferralBanner)
    if (bannerState?.closedAt) {
      setBannerState({
        ...bannerState,
        permanentlyDismissed: true,
      })
    } else {
      setBannerState({
        ...bannerState,
        closedAt: Date.now(),
      })
    }
  }

  const bannerContent = () => {
    if (canSeePromo) {
      return {
        title: i18n.t('marketplace.black_friday_promo.marketplace_list.title'),
        mediaUrl: BlackFridayBannerImage,
        subtitle: `
          ${i18n.t('marketplace.black_friday_promo.marketplace_list.description_1')} ${i18n.t(
          'marketplace.black_friday_promo.marketplace_list.description_2'
        )} ${i18n.t('marketplace.black_friday_promo.marketplace_list.description_3')}
        `,
      }
    } else {
      return {
        title: i18n.t('product_updates.banner.marketplace_product_title'),
        mediaUrl: MarketplaceBannerImage,
        subtitle: i18n.t('product_updates.banner.marketplace_product_description'),
      }
    }
  }

  if ((!shouldShow || !canSeeReferralPage) && !canSelfUpsell) {
    return null
  }

  if (canSelfUpsell) {
    track(trackingEvents.marketplaceModules.viewModulesPageAccess, {
      source: 'Settings Page',
    })
    return (
      <Box paddingX="s48" paddingY="s12" paddingBottom="s24">
        <BaseBanner
          title={bannerContent().title}
          mediaUrl={bannerContent().mediaUrl}
          subtitle={bannerContent().subtitle}
          primaryAction={{
            label: i18n.t('product_updates.banner.button_text'),
            onClick: () => {
              track(trackingEvents.marketplaceModules.goToModulesPage, {
                source: 'Settings Page',
              })
              navigate(navigation.discover.marketplaceModules.fullPath())
            },
            variant: 'default',
          }}
          onClose={handleClose}
        />
      </Box>
    )
  }

  return (
    <Box paddingX="s48" paddingY="s12" paddingBottom="s24">
      <BaseBanner
        title={
          isBrazil
            ? i18n.t(
                'company_settings.general.factorial_referral_program.announcement_banner.title',
                {
                  amount: '$200',
                  max_discount: '$3000',
                }
              )
            : i18n.t('company_settings.general.factorial_referral_program.iphone_air.title_banner')
        }
        mediaUrl={isBrazil ? AmazonCards : BannerImage}
        subtitle={
          canRequestUpsell
            ? isBrazil
              ? i18n.t(
                  'company_settings.general.factorial_referral_program.announcement_banner.subtitle',
                  {
                    amount: '$200',
                    max_discount: '$3000',
                  }
                )
              : i18n.t(
                  'company_settings.general.factorial_referral_program.iphone_air.subtitle_banner'
                )
            : isBrazil
            ? i18n.t(
                'company_settings.general.factorial_referral_program.announcement_banner.subtitle_employee',
                {
                  amount: '$200',
                  max_discount: '$3000',
                }
              )
            : i18n.t(
                'company_settings.general.factorial_referral_program.iphone_air.subtitle_banner_employee'
              )
        }
        primaryAction={{
          label: i18n.t(
            'company_settings.general.factorial_referral_program.announcement_banner.primary_action'
          ),
          onClick: () => {
            track(trackingEvents.settings.clickSettingsReferralBanner)
            navigate(navigation.settings.referralProgram.fullPath())
          },
          variant: 'default',
        }}
        onClose={handleClose}
      />
    </Box>
  )
}

export default ReferalProgramBanner
